import L from 'leaflet';
import markerRenderFunction from './marker-template';

/** Class representing a RLSynoptic map. */
const RLSynoptic = function RLSynoptic(targetDivId, options) {
  this._targetDiv = targetDivId;
  
  this._markerRenderFn = markerRenderFunction;
  
  this._initMap(options);
};

const defaultOptions = {
  zoomRange: [-1, 1],
  doubleClickZoom: false,
  dragImage: true
}

RLSynoptic.prototype._initMap = function _initMap (options = defaultOptions ) {
  const { zoomRange = defaultOptions.zoomRange, doubleClickZoom = false , dragImage = true } = options;
  
  this._mapObj = L.map(this._targetDiv, {
    crs: L.CRS.Simple,
    continuousWorld: false,
    attributionControl: false,
    maxBoundsViscosity: true,
    doubleClickZoom,
    zoomControl: false,
    zoom: 0,
    minZoom: zoomRange[0],
    maxZoom: zoomRange[1]
  });
  if (!dragImage) {
    this._mapObj.dragging.disable();
  }
  L.control
    .zoom({
      position: 'bottomright'
    })
    .addTo(this._mapObj);
  
  // Add 'background' layer to 'map'
  this._backgroundLayer = L.layerGroup().addTo(this._mapObj);
  
  // Add 'markers' layer to 'map'
  this._markersLayer = L.layerGroup().addTo(this._mapObj);
  
  // Add 'expanded marker' layer to map
  this._expMarkersLayer = L.layerGroup().addTo(this._mapObj);
  
  // Add class to map target div
  const maps = document.querySelectorAll(("#" + (this._targetDiv)));
  for (let i = 0; i < maps.length; ++i) {
    maps[i].classList.add('rl-synoptic');
  }
};

  RLSynoptic.prototype.onLoad = function setCallBackOnLoad (onLoadCb) {
  // Setup a new marker template
  this._mapObj.on('load', onLoadCb);
};
/**
 * Set the background image for the Synoptic
 *
 * @param {string} backgroundImageUrl The URL of the image to be displayed on the background
 */
RLSynoptic.prototype.setBackground = function setBackground (backgroundImageUrl) {
  // Retrieve reference to mapObj
  const map = this._mapObj;
  
  // Store reference to background layer
  const _bgLayer = this._backgroundLayer;
  
  // Clear current background image
  _bgLayer.clearLayers();
  
  // Load new background image from supplied URL
  const o = new Image();
  o.onload = function() {
    const n = this.width / this.height;
    if (n) {
      let e, a;
      n > 2 ? (a = (e = 1e3) / n) : (e = (a = 1e3) * n);
      const i = [[0, -e / 2], [a, e / 2]];
      
      const bkgImg = L.imageOverlay(backgroundImageUrl, i).addTo(_bgLayer);
      
      map.imageBounds = bkgImg.getBounds();
      map.fitBounds(i);
      map.setMaxBounds(i);
    } else {
      map.fitBounds([[0, 0], [1e3, 1e3]]);
    }
  };
  
  o.src = backgroundImageUrl;
};

/**
 * Set the function responsible to render the HTML of a Marker
 *
 * @param {RLSynoptic~MarkerRenderFunction} markerRenderFn The function to be invoked when placing a Marker
 */
RLSynoptic.prototype.setMarkerRenderFunction = function setMarkerRenderFunction (markerRenderFn) {
  // Setup a new marker template
  this._markerRenderFn = markerRenderFn;
};

/**
 * Reset the Marker render function to the default one
 */
RLSynoptic.prototype.resetMarkerRenderFunction = function resetMarkerRenderFunction () {
  this._markerRenderFn = markerRenderFunction;
};

/**
 * Set the function responsible to render the HTML of am expanded Marker
 *
 * @param {RLSynoptic~MarkerRenderFunction} markerRenderFn The function to be invoked when expanding a Marker
 */
RLSynoptic.prototype.setExpandedMarkerRenderFunction = function setExpandedMarkerRenderFunction (markerRenderFn) {
  // Setup an expanded marker renderer
  this._expandedMarkerRenderFn = markerRenderFn;
};

/**
 * Reset the Expanded Marker render function
 */
RLSynoptic.prototype.resetExpandedRenderFunction = function resetExpandedRenderFunction () {
  this._expandedMarkerRenderFn = null;
};

/**
 * Add many markers to the map
 * N.B. Will remove all existing markers from the map - same as calling resetMarkers
 *
 * @param {RLSynoptic~Marker[]} markers An array of markers to be inserted on the map
 * @param {RLSynoptic~MarkerOptions} options Marker options
 */
RLSynoptic.prototype.setMarkers = function setMarkers (markers, options) {
  // New markers layer
  const nml = L.layerGroup().addTo(this._mapObj);
  
  // Store ref to old markers layer
  const oml = this._markersLayer;
  
  // Add markers
  for (let idx in markers) {
    this._addMarkerTo(nml, markers[idx], options);
  }
  
  // Remove currently displayed markers
  this.resetMarkers();
  
  // Set new markers layer as current markers layer
  this._markersLayer = nml;
  
  // Remote old markers layer
  oml.remove();
};

/**
 * Add a single marker to the map
 * @param {RLSynoptic~Marker} marker A new marker to be added on the map
 * @param {RLSynoptic~MarkerOptions} options Marker options
 */
RLSynoptic.prototype.addMarker = function addMarker (marker, options) {
  if ( options === void 0 ) options = {};
  
  this._addMarkerTo(this._markersLayer, marker, options);
};

RLSynoptic.prototype.removeMarker = function removeMarker (marker) {
  
  this._removeMarker(marker);
};

/**
 * Remove all markers from the map
 */
RLSynoptic.prototype.resetMarkers = function resetMarkers () {
  this._markersLayer.clearLayers();
};

/**
 * Set draggability for displayed markers
 *
 * @param {boolean} isDraggable Set draggability for Markers
 */
RLSynoptic.prototype.setMarkersDraggable = function setMarkersDraggable (isDraggable) {
  this._markersLayer.eachLayer(function(layer) {
    if (layer.dragging) {
      isDraggable ? layer.dragging.enable() : layer.dragging.disable();
    }
  });
};

/**
 * Add a click listener to the map
 *
 * @param {RLSynoptic~mapClickCallbackFn} mapClickCallbackFn The function to be called when the user clicks on the map
 */
RLSynoptic.prototype.attachMapClickListener = function attachMapClickListener (mapClickCallbackFn) {
  // Attach map click listener
  this._mapObj.on('click', this._handleMapClickEvent(mapClickCallbackFn));
};

/**
 * Add a click listener to be called for every marker click
 *
 * @param {RLSynoptic~markerClickCallbackFn} markerClickCallbackFn The function to be called when the user clicks on a marker
 */
RLSynoptic.prototype.attachMarkerClickListener = function attachMarkerClickListener (markerClickCallbackFn) {
  // Store marker click event listener
  this._markerClickHandler = markerClickCallbackFn;
};

/**
 * Add a drag listener to be called for every marker dragend event
 *
 * @param {callbackFn} markerDragCallbackFn
 */
RLSynoptic.prototype.attachMarkerDragListener = function attachMarkerDragListener (markerDragCallbackFn) {
  // Store marker dragend event listener
  this._markerDragHandler = markerDragCallbackFn;
};

// PRIVATE METHODS

RLSynoptic.prototype._removeMarker = function _removeMarkerTo (marker) {
  const ref$1 = this._markerRenderFn(marker);
  const html = ref$1.html;
  const size = ref$1.size;
  L.marker([marker.x, marker.y], {
    icon: new L.DivIcon({
      className: 'rl-marker',
      html: html,
      iconSize: size
    })
  }).remove();
};

RLSynoptic.prototype._addMarkerTo = function _addMarkerTo (targetLayer, marker, options) {
  const ref = options || {};
  const draggable = ref.draggable;
  
  const ref$1 = this._markerRenderFn(marker);
  const html = ref$1.html;
  const size = ref$1.size;
  const m = L.marker([marker.x, marker.y], {
    draggable: draggable,
    icon: new L.DivIcon({
      className: 'rl-marker',
      html: html,
      iconSize: size
    })
  }).addTo(targetLayer);
  
  m.on('click', (e) => this._handleMarkerClickEvent(marker, e));
  m.on('dragend', this._handleMarkerDragEvent(marker));
};

RLSynoptic.prototype._handleMapClickEvent = function _handleMapClickEvent (callbackFn) {
  const _resetExpMarkers = this._resetExpandedMarker.bind(this);
  
  return function(leafletClickEvent) {
    // Reset expanded markers
    _resetExpMarkers();
    
    // Callback function
    const latlng = leafletClickEvent.latlng;
    callbackFn({ x: latlng.lat, y: latlng.lng });
  };
};

RLSynoptic.prototype._handleMarkerClickEvent = function _handleMarkerClickEvent (markerData, e) {
  // Handle marker click - if needed
  this._markerClickHandler && this._markerClickHandler(markerData, e);
};

RLSynoptic.prototype._handleMarkerDragEvent = function _handleMarkerDragEvent (markerData) {
  const this$1 = this;
  
  return function (leafletDragEvent) {
    const newMarkerLocation = leafletDragEvent.target.getLatLng();
    this$1._markerDragHandler &&
    this$1._markerDragHandler(markerData, { x: newMarkerLocation.lat, y: newMarkerLocation.lng });
  };
};

RLSynoptic.prototype._expandMarker = function _expandMarker (marker) {
  if (!marker.expandable || !this._expandedMarkerRenderFn) {
    return false;
  }
  
  const posX = marker.x;
  const posY = marker.y;
  const ref = this._expandedMarkerRenderFn(marker);
  const html = ref.html;
  const size = ref.size;
  L.marker([posX, posY], {
    icon: new L.DivIcon({
      className: 'rl-marker expanded',
      html: html,
      iconSize: size
    })
  }).addTo(this._expMarkersLayer);
  
  return true;
};

RLSynoptic.prototype._resetExpandedMarker = function _resetExpandedMarker () {
  this._expMarkersLayer.clearLayers();
};

/**
 * A set of options to be applied to a Map.
 * @name MapOptions
 * @typedef {Object} RLSynoptic~MapOptions
 * @property {number[]} [zoomRange=[-1, 1]] - Specify the Map zoom range as [minZoom, maxZoom].
 */

/**
 * The function to be called when the user clicks on the Map.
 * @callback RLSynoptic~mapClickCallbackFn
 * @param {RLSynoptic~PointCoordinates} coordinates The coordinates of the click.
 */

/**
 * The function to be called when the user clicks on a Marker.
 * @callback RLSynoptic~markerClickCallbackFn
 * @param {Marker} marker The clicked marker.
 */

/**
 * The coordinates of a Point.
 * @typedef {Object} RLSynoptic~PointCoordinates
 * @property {number} x - The X coordinate.
 * @property {number} y - The Y coordinate.
 */

/**
 * A Marker to be displayed on the map.
 * @name Marker
 * @typedef {Object} RLSynoptic~Marker
 * @property {number} x - The X coordinate of the marker.
 * @property {number} y - The Y coordinate of the marker.
 * @property {boolean} [expandable] - Indicates that the marker should expand.
 * @property {any} [name] - Any custom property.
 */

/**
 * A set of options to be applied to a Marker.
 * @name MarkerOptions
 * @typedef {Object} RLSynoptic~MarkerOptions
 * @property {boolean} draggable - Specify if the Marker should be draggable.
 */

/**
 * A function responsible to render a Marker on the map
 * @name MarkerRenderFunction
 * @typedef {Function} RLSynoptic~MarkerRenderFunction
 * @param {RLSynoptic~Marker} marker The marker data to be rendered
 * @return {RLSynoptic~MarkerRenderProperties} The marker HTML and sizing to be added to the map
 */

/**
 * @name MarkerRenderProperties
 * @typedef {Object} RLSynoptic~MarkerRenderProperties
 * @property {string} html The HTML to be added to the DOM
 * @property {number[]} size An array [width, height] containing the actual sizing of the marker
 */

export default RLSynoptic;
